import React from 'react';
import {
  Grid,
  LinearProgress,
  CardContent,
  Typography,
  Card,
  Button,
  Box,
} from '@material-ui/core';
import { useIsFetching } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import Calendar from 'components/Calendar/Calendar';
import SingleMetricCard from 'components/common/SingleMetricCard';
import DetailedMetricCard from 'components/common/DetailedMetricCard';
import JobsByStatusChart from 'components/Jobs/Graphs/JobsByStatusChart';
import TopClientsByVolumeChart from 'components/Jobs/Graphs/JobsByClientChart';
import JobStatusByClientChart from 'components/Jobs/Graphs/JobStatusByClientChart';
import PeopleIcon from '@material-ui/icons/PeopleOutlined';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import JobsByCoordinatorChart from 'components/Jobs/Graphs/JobsByCoordinatorChart';
import JobHealthByCoordinatorChart from 'components/Jobs/Graphs/JobHealthByCoordinatorChart';
import NeedsAttentionCard from 'components/Jobs/Graphs/NeedsAttentionCard';
import type { DashboardView } from 'views/Dashboard/DashboardTabs';
import { ACTIVE_JOB_STATUSES, JobTableType } from 'api/jobApi';
import { buildJobListUrl } from 'components/Jobs/jobListUrlParams';

interface Props {
  /** Active dashboard tab. Only the active tab mounts, so React Query does
   *  not fetch the others. */
  view: DashboardView;
}

/**
 * Metric tiles — the at-a-glance layer. Rendered by `DashboardTabs` above the
 * tab pills because the cards are fixed across tabs.
 */
export const MetricStrip = ({ data }: { data: any }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Drill-through for the active job count. The statuses are passed
  // explicitly (not just `tab=open`) so the list matches the card's count —
  // the open tab alone would also include Not started jobs.
  const viewActiveJobs = () =>
    navigate(
      buildJobListUrl({
        tab: JobTableType.OPEN,
        status: ACTIVE_JOB_STATUSES,
      })
    );

  // The average summarises the coordinator workload chart, so the card hands
  // off to that tab using the same `?view=` mechanism the tab pills use.
  const viewWorkloadTab = () => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'workload');
    setSearchParams(next, { replace: true });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        <DetailedMetricCard
          metric={data?.userCount}
          label="ACTIVE USERS"
          details={[
            { subMetric: data?.projectCoordinatorCount, label: 'Project Coordinators' },
            { subMetric: data?.technicianCount, label: 'Technicians' },
          ]}
          icon={<PeopleIcon />}
          onClick={() => navigate('/administration/users')}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <SingleMetricCard
          metric={data?.activeJobCount}
          label="ACTIVE JOBS"
          description="The number of jobs that are in progress, on hold, approval pending or to be invoiced. Jobs that have not started, canceled and completed are excluded from this count."
          icon={<AssignmentIcon />}
          onClick={viewActiveJobs}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <DetailedMetricCard
          metric={data?.jobsPerCoordinator}
          label="ACTIVE JOBS PER COORDINATOR"
          details={[
            { subMetric: data?.projectCoordinatorCount, label: 'Project Coordinators' },
            {
              subMetric: data?.activeJobCount,
              label: 'Active Jobs',
              onClick: viewActiveJobs,
            },
          ]}
          icon={<AssignmentIndIcon />}
          onClick={viewWorkloadTab}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <DetailedMetricCard
          metric={data?.technicianUtilizationRate}
          percentage={true}
          label="TECHNICIAN UTILIZATION"
          details={[
            { subMetric: data?.technicianCount, label: 'Technicians' },
            {
              subMetric: data?.technicianUtilization,
              label: 'Assigned to Tasks Today',
            },
          ]}
          icon={<PeopleIcon />}
          // Utilization measures technicians assigned to today's tasks —
          // the calendar is where those assignments live.
          onClick={() => navigate('/calendar')}
        />
      </Grid>
    </Grid>
  );
};

const TodayTasksCard = () => {
  const navigate = useNavigate();
  const isFetchingCalendar = useIsFetching(['calendar-events', 'today']);
  return (
    <Grid item xs={12} md={12} lg={3}>
      <Card style={{ borderRadius: '12px', height: '100%' }}>
        {isFetchingCalendar > 0 && <LinearProgress />}
        <CardContent>
          <Box mb={2}>
            <Typography variant="overline">Your day at a glance</Typography>
            <Typography variant="h4" gutterBottom>
              Scheduled tasks for Today
            </Typography>
          </Box>
          <Calendar
            showToolbar={false}
            defaultView={'day'}
            step={30}
          />
          <Button
            variant="outlined"
            style={{ marginTop: '2rem' }}
            size="small"
            color="primary"
            onClick={() => navigate('/calendar')}>
            Open calendar
          </Button>
        </CardContent>
      </Card>
    </Grid>
  );
};

/** Overview: Jobs by Status · Needs attention · Today's tasks */
const OverviewTab = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6} lg={4}>
      <JobsByStatusChart />
    </Grid>
    <Grid item xs={12} md={6} lg={5}>
      <NeedsAttentionCard />
    </Grid>
    <TodayTasksCard />
  </Grid>
);

/** Workload & Health: Coordinator Workload · Job Health by Coordinator */
const WorkloadHealthTab = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={12} lg={6}>
      <JobsByCoordinatorChart />
    </Grid>
    <Grid item xs={12} md={12} lg={6}>
      <JobHealthByCoordinatorChart />
    </Grid>
  </Grid>
);

/** Clients: Top Clients by Volume · Job Status by Client */
const ClientsTab = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={12} lg={6}>
      <TopClientsByVolumeChart />
    </Grid>
    <Grid item xs={12} md={12} lg={6}>
      <JobStatusByClientChart />
    </Grid>
  </Grid>
);

const AdminGridItems = ({ view }: Props) => {
  return (
    <>
      {view === 'overview' && <OverviewTab />}
      {view === 'workload' && <WorkloadHealthTab />}
      {view === 'clients' && <ClientsTab />}
    </>
  );
};

export default AdminGridItems;
